import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { SentimentBadge, IntentScore, StatusBadge } from "@/components/Badges";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowUpRight, Filter, TestTube2, Trash2, ArrowUp, AlertTriangle } from "lucide-react";

const filters = ["Pending", "Orchestrating", "Active", "Completed", "Archived"];

export default function AdminDashboard() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("Pending");
  const [confirmPurge, setConfirmPurge] = useState(false);

  const stats = useQuery({ queryKey: ["admin-stats"], queryFn: () => api.get("/stats/admin").then(r => r.data), refetchInterval: 15000 });
  const threads = useQuery({
    queryKey: ["threads", filter],
    queryFn: () => api.get(`/threads?status=${filter}`).then(r => r.data),
    refetchInterval: 10000,
  });

  const seedMut = useMutation({
    mutationFn: () => api.post("/dev/seed-sample-thread"),
    onSuccess: () => { toast.success("Sample thread ingested"); qc.invalidateQueries({queryKey:["threads"]}); qc.invalidateQueries({queryKey:["admin-stats"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Seeding failed"),
  });

  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/threads/${id}`),
    onSuccess: () => { toast.success("Thread deleted"); qc.invalidateQueries({queryKey:["threads"]}); qc.invalidateQueries({queryKey:["admin-stats"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const promoteMut = useMutation({
    mutationFn: (id) => api.post(`/threads/${id}/promote`),
    onSuccess: () => { toast.success("Promoted to Pending"); qc.invalidateQueries({queryKey:["threads"]}); qc.invalidateQueries({queryKey:["admin-stats"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Promote failed"),
  });

  const purgeMut = useMutation({
    mutationFn: () => api.post("/threads/purge", { status: filter }),
    onSuccess: (r) => { toast.success(`Purged ${r.data.deleted} thread(s)`); setConfirmPurge(false); qc.invalidateQueries({queryKey:["threads"]}); qc.invalidateQueries({queryKey:["admin-stats"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Purge failed"),
  });

  const handleDelete = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm("Delete this thread and its campaign/tasks?")) {
      delMut.mutate(id);
    }
  };

  const handlePromote = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    promoteMut.mutate(id);
  };

  const canPurge = ["Archived", "Completed"].includes(filter);

  return (
    <Layout
      title="High-Priority Queue."
      subtitle="Live ingestion · commercial intent > 0.75"
      right={
        <div className="flex items-center gap-2">
          {canPurge && (threads.data?.length || 0) > 0 && (
            <button
              onClick={() => setConfirmPurge(true)}
              data-testid="purge-btn"
              className="border border-[#FF3B30]/40 hover:border-[#FF3B30] text-[#FF3B30] px-3 py-2 text-[12px] font-medium tracking-wide flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Purge {filter}
            </button>
          )}
          <button
            onClick={() => seedMut.mutate()}
            disabled={seedMut.isPending}
            data-testid="seed-sample-thread-btn"
            className="border border-white/10 hover:border-white/30 px-3 py-2 text-[12px] font-medium tracking-wide flex items-center gap-2 disabled:opacity-40"
          >
            {seedMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
            Seed sample thread
          </button>
        </div>
      }
    >
      {/* Purge confirmation modal */}
      {confirmPurge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setConfirmPurge(false)}>
          <div className="w-full max-w-md bg-[#09090B] border border-[#FF3B30]/40 p-6" onClick={(e)=>e.stopPropagation()} data-testid="purge-confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-[#FF3B30] shrink-0"/>
              <div>
                <div className="font-display font-black text-xl tracking-tight">Purge all {filter} threads?</div>
                <div className="text-[13px] text-zinc-400 mt-1">This deletes {threads.data?.length || 0} thread(s) and all associated campaigns/tasks. Cannot be undone.</div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirmPurge(false)} className="flex-1 border border-white/15 hover:border-white/30 py-2 text-[13px]">Cancel</button>
              <button
                onClick={() => purgeMut.mutate()}
                disabled={purgeMut.isPending}
                data-testid="purge-confirm-btn"
                className="flex-1 bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2 text-[13px] flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {purgeMut.isPending && <Loader2 className="w-4 h-4 animate-spin"/>}
                CONFIRM PURGE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-0 border border-white/10 mb-10">
        {[
          ["Pending", stats.data?.pending_threads],
          ["Active", stats.data?.active_campaigns],
          ["Completed", stats.data?.completed],
          ["Archived", stats.data?.archived],
          ["Total Agents", stats.data?.total_agents],
          ["Active", stats.data?.active_agents, "agents-active"],
          ["Suspended", stats.data?.suspended_agents, "agents-suspended"],
          ["Tasks", stats.data?.total_tasks],
        ].map(([label, val, tid], i) => (
          <div key={i} data-testid={`stat-${tid || label.toLowerCase().replace(/\s+/g,'-')}`} className={`px-5 py-4 ${i>0?"border-t md:border-t-0 md:border-l border-white/10":""}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
            <div className="font-display font-black text-3xl tracking-tighter">{val ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter className="w-4 h-4 text-zinc-500" />
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase()}`}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest border ${
              filter === f ? "bg-white text-black border-white" : "border-white/10 text-zinc-400 hover:text-white hover:border-white/30"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Thread list */}
      <div className="border border-white/10">
        {threads.isLoading && (
          <div className="p-8 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Fetching threads…</div>
        )}
        {threads.data?.length === 0 && (
          <div className="p-12 text-center">
            <div className="font-mono text-[11px] text-zinc-500 uppercase tracking-widest mb-2">No threads matching “{filter}”</div>
            <div className="text-zinc-400 text-sm">
              {filter === "Archived"
                ? "Archived threads scored below the 0.75 commercial-intent threshold."
                : "Configure Syften to point at your webhook, or Pull recent matches from Webhook settings."}
            </div>
          </div>
        )}
        {threads.data?.map((t, idx) => (
          <div
            key={t.id}
            data-testid={`thread-row-${t.id}`}
            className={`relative block px-5 py-5 hover:bg-[#0e0e10] transition-colors ${idx>0?"border-t border-white/10":""} ${t.status==="Pending"?"tracer":""}`}
          >
            <Link to={filter === "Archived" ? "#" : `/admin/threads/${t.id}`}
                  onClick={(e) => filter === "Archived" && e.preventDefault()}
                  className="block">
              <div className="flex items-start gap-4">
                <div className="pt-0.5">
                  <IntentScore score={t.commercial_intent_score} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[11px] text-[#FF3B30]">{t.subreddit || "r/?"}</span>
                    <span className="font-mono text-[11px] text-zinc-600">·</span>
                    <span className="font-mono text-[11px] text-zinc-500">u/{t.author || "unknown"}</span>
                    <span className="font-mono text-[11px] text-zinc-600">·</span>
                    <span className="font-mono text-[11px] text-zinc-500">filter: {t.filter_matched || "—"}</span>
                  </div>
                  <div className="text-[15px] text-zinc-100 font-medium mb-1 truncate">{t.title || "(untitled)"}</div>
                  <div className="text-[13px] text-zinc-400 line-clamp-2">{t.ingested_text}</div>
                  {t.intent_reason && filter === "Archived" && (
                    <div className="mt-2 font-mono text-[11px] text-zinc-500">// why archived: {t.intent_reason}</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <StatusBadge status={t.status} />
                  <SentimentBadge value={t.syften_sentiment} />
                  {filter !== "Archived" && <ArrowUpRight className="w-4 h-4 text-zinc-600" />}
                </div>
              </div>
            </Link>
            {/* Action buttons */}
            <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
              {filter === "Archived" && (
                <button
                  onClick={(e) => handlePromote(e, t.id)}
                  data-testid={`promote-btn-${t.id}`}
                  className="border border-emerald-500/40 hover:border-emerald-500 text-emerald-400 hover:text-emerald-300 px-2 py-1 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1"
                >
                  <ArrowUp className="w-3 h-3"/> Promote to Pending
                </button>
              )}
              <button
                onClick={(e) => handleDelete(e, t.id)}
                data-testid={`delete-btn-${t.id}`}
                className="border border-white/10 hover:border-[#FF3B30] text-zinc-500 hover:text-[#FF3B30] px-2 py-1 text-[10px] font-mono uppercase tracking-widest flex items-center gap-1 ml-auto"
              >
                <Trash2 className="w-3 h-3"/> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );
}
